import React, { useEffect, useState } from "react";
import { FileUploader } from "react-drag-drop-files";
import { useParams } from "react-router-dom";
import DashboardContent from "../../components/DashboardContent";
import useModalWindow from "../../hooks/useModalWindow";
import useCsr from "../../hooks/useCsr";
import { CsrAPI } from "../../apis/CsrAPI";
import ProgressBar from "../../utils/ProgressBar";
import Loader from "../../utils/Loader";
import {
  FILE_UPLOAD_STATUS,
  FILES_STATUS,
  SCS_DOCUMENT_TYPE,
} from "../../utils/Constants";
import * as Sentry from "@sentry/react";

const isProduction = process.env.NODE_ENV === "production";
import RedirectHandler from "../../components/RedirectHandler";
import { ToastContainer } from "react-toastify";
import { ReactComponent as DeleteFile } from "../../assets/images/icons/delete-small.svg";
import { ReactComponent as FileUploaded } from "../../assets/images/icons/file-uploaded.svg";
import { ReactComponent as FileProcessing } from "../../assets/images/icons/file-processing.svg";
import { ReactComponent as FileError } from "../../assets/images/icons/file-error.svg";
import { ReactComponent as FileProcessed } from "../../assets/images/icons/file-processed.svg";
import { ReactComponent as BinBlack } from "../../assets/images/icons/bin-black.svg";
import { ReactComponent as RefreshBlack } from "../../assets/images/icons/refresh-black.svg";
import UploadStatusBar from "../../components/UploadStatusBar";
import { Tooltip } from "react-tooltip";
import { useFeatureFlags } from "../../contexts/FeatureFlagContext";

const ALLOWED_FILE_TYPES = ["DOCX", "RTF"];
const MAX_FILE_SIZE_MB = 200;

const CONTROLS = [
  {
    docType: SCS_DOCUMENT_TYPE.gids,
    listKey: "gids",
    label: "GIDB",
    addLabel: "Add GIDB",
    changeLabel: "Change GIDB file",
    addMoreLabel: "Add additional GIDB",
    multiple: true,
  },
  {
    docType: SCS_DOCUMENT_TYPE.sdap,
    listKey: "sdap",
    label: "SAP/SDAP",
    addLabel: "Add SAP/SDAP",
    changeLabel: "Change SAP/SDAP file",
    addMoreLabel: "Add additional SAP/SDAP",
    multiple: false,
  },
  {
    docType: SCS_DOCUMENT_TYPE.csrs,
    listKey: "csrs",
    label: "Individual CSRs",
    addLabel: "Add individual CSRs",
    changeLabel: "Add individual CSRs",
    addMoreLabel: "Add additional CSRs",
    multiple: true,
  },
  {
    docType: SCS_DOCUMENT_TYPE.iso,
    listKey: "iso",
    label: "ISO",
    addLabel: "Add ISO",
    changeLabel: "Add ISO",
    addMoreLabel: "Add additional ISO",
    multiple: true,
  },
];

let documentType = "";
let changeFileHelper = {};

const SCSFileUpload = () => {
  const { studyId: summaryId } = useParams();
  const { modalWindow, handleCancel } = useModalWindow();
  const {
    setAjaxLoading,
    errMsg,
    setErrMsg,
    errRef,
    setScsFilesStatus,
    getSafetyDocuments,
    gidsProgressRef,
    sdapProgressRef,
    csrsProgressRef,
    isoProgressRef,
    gidsProgress,
    setGidsProgress,
    sdapProgress,
    setSdapProgress,
    csrsProgress,
    setCsrsProgress,
    isoProgress,
    setIsoProgress,
    scsDocumentsList,
    wrongEnvironment,
  } = useCsr();
  const { ajaxLoading } = useFeatureFlags();
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [showRedirect, setShowRedirect] = useState(false);

  const progressByType = {
    [SCS_DOCUMENT_TYPE.gids]: {
      ref: gidsProgressRef,
      value: gidsProgress,
      setter: setGidsProgress,
    },
    [SCS_DOCUMENT_TYPE.sdap]: {
      ref: sdapProgressRef,
      value: sdapProgress,
      setter: setSdapProgress,
    },
    [SCS_DOCUMENT_TYPE.csrs]: {
      ref: csrsProgressRef,
      value: csrsProgress,
      setter: setCsrsProgress,
    },
    [SCS_DOCUMENT_TYPE.iso]: {
      ref: isoProgressRef,
      value: isoProgress,
      setter: setIsoProgress,
    },
  };

  useEffect(() => {
    const allDocs = [
      ...(scsDocumentsList?.gids || []),
      ...(scsDocumentsList?.sdap || []),
      ...(scsDocumentsList?.csrs || []),
      ...(scsDocumentsList?.iso || []),
    ].map(doc => ({
      filename: doc.filename,
      status: doc.status,
      document_type: doc.document_type,
    }));

    setUploadedDocuments(prev => {
      const updatedDocs = [...allDocs];

      prev.forEach(localDoc => {
        if (localDoc.status === FILE_UPLOAD_STATUS.uploading) {
          const existsInApi = allDocs.some(
            apiDoc =>
              apiDoc.filename.split(".")[0] ===
                localDoc.filename.split(".")[0] &&
              apiDoc.document_type === localDoc.document_type
          );

          if (!existsInApi) {
            updatedDocs.push(localDoc);
          }
        }
      });

      return updatedDocs;
    });
  }, [scsDocumentsList]);

  function isIterable(obj) {
    if (obj == null) return false;
    return typeof obj[Symbol.iterator] === "function";
  }

  const resetAllProgress = () => {
    setGidsProgress({ val: [] });
    setSdapProgress({ val: [] });
    setCsrsProgress({ val: [] });
    setIsoProgress({ val: [] });
  };

  const handleApiError = error => {
    if (isProduction) {
      Sentry.captureException(error);
    }
    if (!error?.response) {
      setErrMsg("No Server Response");
    } else if (error.response?.status === 401) {
      setErrMsg("Invalid user credentials");
    } else if (error.response?.status === 403) {
      setShowRedirect(true);
    } else {
      setErrMsg("Fail to upload document");
    }
    errRef.current?.focus();
  };

  const upload = (idx, file, docType) => {
    setScsFilesStatus(FILES_STATUS.progress);
    const progress = progressByType[docType];
    /* eslint-disable no-unsafe-optional-chaining */
    let _progress = isIterable(progress?.ref?.current?.val)
      ? [...progress.ref.current.val]
      : [];
    /* eslint-enable no-unsafe-optional-chaining */

    return CsrAPI.uploadSafetySummaryDocument(
      { summaryId, file, docType },
      event => {
        _progress[idx].percentage = Math.round(
          (100 * event.loaded) / event.total
        );
        progress.setter({ val: _progress });
      }
    )
      .then(() => {
        _progress[idx].percentage = -1;
        progress.setter({ val: _progress });
        getSafetyDocuments(summaryId);
      })
      .catch(error => {
        _progress[idx].percentage = 0;
        progress.setter({ val: _progress });
        handleApiError(error);
      });
  };

  const handleChange = file => {
    if (Object.keys(changeFileHelper).length > 0) {
      CsrAPI.deleteSafetySummaryDocument({
        summaryId: changeFileHelper.summaryId,
        fileName: changeFileHelper.fileName,
      })
        .then(() => {
          getSafetyDocuments(summaryId);
          changeFileHelper = {};
        })
        .catch(handleApiError);
    }

    let files = [];
    if (file instanceof File) {
      files[0] = file;
    } else {
      files = Array.from(file);
    }

    const newUploadedDocs = files.map(f => ({
      document_type: documentType,
      filename: f.name,
      status: FILE_UPLOAD_STATUS.uploading,
    }));

    setUploadedDocuments(prev => [...prev, ...newUploadedDocs]);

    const _progressInfos = files.map(f => ({
      percentage: 0,
      fileName: f.name,
    }));

    const progress = progressByType[documentType];
    if (progress) {
      progress.ref.current = { val: _progressInfos };
    }

    const uploadPromises = files.map((f, i) => upload(i, f, documentType));

    Promise.all(uploadPromises)
      .then(() => {
        getSafetyDocuments(summaryId);
      })
      .then(() => {
        resetAllProgress();
      });

    handleCancel();
  };

  const handleUploadPopup = (docType, displayLabel) => {
    documentType = docType;
    const isMultiple =
      docType === SCS_DOCUMENT_TYPE.csrs ||
      docType === SCS_DOCUMENT_TYPE.iso ||
      docType === SCS_DOCUMENT_TYPE.gids;

    modalWindow({
      title: "Add " + displayLabel,
      message: (
        <FileUploader
          handleChange={handleChange}
          name="file"
          types={ALLOWED_FILE_TYPES}
          maxSize={MAX_FILE_SIZE_MB}
          onTypeError={() =>
            setErrMsg("Only .docx and .rtf files are allowed.")
          }
          onSizeError={() =>
            setErrMsg(`File size must be ${MAX_FILE_SIZE_MB} MB or less.`)
          }
          label={"Upload or Drop your files here to upload"}
          classes={"drop_area"}
          multiple={isMultiple}
        />
      ),
      confirmButtonLabel: "",
    });
  };

  const handleFileChange = (fileName, docType, displayLabel) => {
    changeFileHelper = { summaryId, fileName };
    handleUploadPopup(docType, displayLabel);
  };

  const handleFileDelete = fileName => {
    setAjaxLoading(true);

    setUploadedDocuments(prev => prev.filter(doc => doc.filename !== fileName));

    CsrAPI.deleteSafetySummaryDocument({ summaryId, fileName })
      .then(() => {
        getSafetyDocuments(summaryId);
      })
      .catch(handleApiError)
      .finally(() => {
        setAjaxLoading(false);
      });

    handleCancel();
  };

  const handleDeleteAll = async () => {
    const confirmed = await modalWindow({
      title: "Delete all uploaded files",
      message:
        "Do you want to delete all previously uploaded files for this Summary of Clinical Safety?",
      confirmButtonLabel: "Yes",
    });

    if (!confirmed) return;

    setAjaxLoading(true);
    CsrAPI.deleteAllSafetySummaryDocuments({ summaryId })
      .then(() => {
        resetAllProgress();
        setUploadedDocuments([]);
        setScsFilesStatus(FILES_STATUS.readyEmpty);
        getSafetyDocuments(summaryId);
      })
      .catch(error => {
        if (isProduction) {
          Sentry.captureException(error);
        }
        if (!error?.response) {
          setErrMsg("No Server Response");
        } else if (error.response?.status === 401) {
          setErrMsg("Invalid user credentials");
        } else if (error.response?.status === 403) {
          setShowRedirect(true);
        } else {
          setErrMsg("Failed to delete all files");
        }
        errRef.current?.focus();
      })
      .finally(() => {
        setAjaxLoading(false);
      });
  };

  const handleReloadAll = async () => {
    const confirmed = await modalWindow({
      title: "Warning",
      message:
        "Performing a refresh may impact selected wizard answers and modify associated data mappings. Please ensure you review all entries after refreshing.",
      confirmButtonLabel: "OK",
    });

    if (!confirmed) return;

    setAjaxLoading(true);
    CsrAPI.reloadSafetySummaryDocuments({ summaryId })
      .then(() => {
        getSafetyDocuments(summaryId);
      })
      .catch(error => {
        if (isProduction) {
          Sentry.captureException(error);
        }
        if (!error?.response) {
          setErrMsg("No Server Response");
        } else if (error.response?.status === 401) {
          setErrMsg("Invalid user credentials");
        } else if (error.response?.status === 403) {
          setShowRedirect(true);
        } else {
          setErrMsg("Failed to refresh all files");
        }
        errRef.current?.focus();
      })
      .finally(() => {
        setAjaxLoading(false);
      });
  };

  const isAnyFileUploading = uploadedDocuments.some(
    doc => doc.status === FILE_UPLOAD_STATUS.uploading
  );

  const hasAnyFiles =
    (scsDocumentsList?.gids?.length || 0) +
      (scsDocumentsList?.sdap?.length || 0) +
      (scsDocumentsList?.csrs?.length || 0) +
      (scsDocumentsList?.iso?.length || 0) >
    0;

  const renderControlSection = control => {
    const docs = scsDocumentsList?.[control.listKey] || [];
    const progress = progressByType[control.docType]?.value;

    if (docs.length > 0 || (progress && progress?.val?.length > 0)) {
      return (
        <div key={control.docType}>
          <label className="file-upload-label">{control.addLabel}</label>
          <div className={"csr-file-upload__component-uploaded"}>
            <div className={"csr-file-upload__component-uploaded__left"}>
              {docs.map(doc => (
                <div
                  key={`${control.listKey}-${doc.filename}`}
                  className={"csr-file-upload__component-uploaded__doc"}>
                  {doc.status === FILE_UPLOAD_STATUS.processing ? (
                    <FileProcessing />
                  ) : doc.status === FILE_UPLOAD_STATUS.processed ? (
                    <FileProcessed />
                  ) : doc.status === FILE_UPLOAD_STATUS.errored ? (
                    <FileError />
                  ) : (
                    <FileUploaded />
                  )}

                  <label>{doc.filename}</label>
                  <button
                    className={"delete"}
                    onClick={() => handleFileDelete(doc.filename)}>
                    <DeleteFile />
                  </button>
                </div>
              ))}

              {progress &&
                progress?.val?.length > 0 &&
                progress?.val?.map((info, index) => (
                  <div
                    key={`${control.listKey}-progress-${index}`}
                    className={`csr-file-upload__component-uploading${info.percentage === -1 ? " hide" : ""}`}>
                    <label>{info.fileName}</label>
                    <div className={"progress-bar__wrapper"}>
                      <Loader loading={true} />
                      <ProgressBar completed={info.percentage} />
                    </div>
                  </div>
                ))}
            </div>
            <div className={"csr-file-upload__component-uploaded__right"}>
              <button
                className={
                  "input-submit input-submit__tertiary input-submit__small"
                }
                onClick={() => {
                  if (control.multiple) {
                    handleUploadPopup(control.docType, control.label);
                  } else {
                    handleFileChange(
                      docs[0].filename,
                      control.docType,
                      control.label
                    );
                  }
                }}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ marginRight: "8px" }}>
                  <path
                    d="M8 2L8 10M8 10L5 7M8 10L11 7M2 12L14 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {control.multiple ? control.addMoreLabel : control.changeLabel}
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div key={control.docType} className={"csr-file-upload__component"}>
        <label>{control.addLabel}</label>
        <button
          className={"input-submit input-submit__primary input-submit__small"}
          onClick={() => handleUploadPopup(control.docType, control.label)}>
          {control.addLabel}
        </button>
      </div>
    );
  };

  const PageContent = () => (
    <div className={"csr-file-upload csr-file-upload__wrapper"}>
      <UploadStatusBar documents={uploadedDocuments} />

      <span
        ref={errRef}
        className={errMsg ? "errmsg" : "offscreen"}
        aria-live="assertive">
        {errMsg}
      </span>

      {hasAnyFiles && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "8px",
            paddingTop: "16px",
          }}>
          <button
            style={{
              background: "none",
              border: "none",
              cursor: isAnyFileUploading ? "not-allowed" : "pointer",
              padding: 0,
              opacity: isAnyFileUploading ? 0.5 : 1,
            }}
            disabled={isAnyFileUploading}
            data-tooltip-id="scs-delete-all"
            onClick={handleDeleteAll}>
            <BinBlack />
          </button>
          <Tooltip
            id="scs-delete-all"
            place="top"
            delayShow={100}
            className="custom-tooltip">
            Delete all uploaded files
          </Tooltip>
          <button
            style={{
              background: "none",
              border: "none",
              cursor: isAnyFileUploading ? "not-allowed" : "pointer",
              padding: 0,
              opacity: isAnyFileUploading ? 0.5 : 1,
            }}
            disabled={isAnyFileUploading}
            data-tooltip-id="scs-refresh-all"
            onClick={handleReloadAll}>
            <RefreshBlack />
          </button>
          <Tooltip
            id="scs-refresh-all"
            place="top"
            delayShow={100}
            className="custom-tooltip">
            Refresh all files
          </Tooltip>
        </div>
      )}

      {CONTROLS.map(control => renderControlSection(control))}

      <ToastContainer
        position="top-right"
        autoClose={2000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={true}
        rtl={false}
        pauseOnFocusLoss={false}
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );

  if (showRedirect || wrongEnvironment) {
    return <RedirectHandler />;
  }

  if (ajaxLoading) {
    return <Loader />;
  }

  return (
    <DashboardContent
      sidebarCSR={true}
      headerTitle={"Upload your files"}
      headerButton={false}
      pageContent={PageContent()}
      prevButton={{
        buttonTitle: "Summary Name",
        buttonGoToAction: summaryId
          ? `/scs/${summaryId}/summary`
          : "/scs/new/summary",
      }}
      nextButton={{
        buttonTitle: "Description of Studies",
        buttonGoToAction: `/scs/${summaryId}/description-of-studies`,
      }}
    />
  );
};

export default SCSFileUpload;
